import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const ButtonStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.small ? 'var(--space-3) var(--space-4)' : 'var(--space-4) var(--space-8)'};
  border-radius: ${props => props.rounded ? '50px' : 'var(--border-radius-lg)'};
  font-weight: 600;
  font-size: ${props => props.small ? 'var(--font-size-sm)' : 'var(--font-size-base)'};
  letter-spacing: 0.025em;
  text-align: center;
  cursor: pointer;
  transition: all var(--transition-normal);
  text-decoration: none;
  border: 2px solid transparent;
  position: relative;
  overflow: hidden;
  font-family: 'Inter', sans-serif;
  
  svg {
    margin-right: ${props => props.iconOnly ? '0' : 'var(--space-2)'};
    font-size: ${props => props.small ? 'var(--font-size-sm)' : 'var(--font-size-lg)'};
    transition: transform var(--transition-normal);
  }
  
  &:hover svg {
    transform: translateX(2px);
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  ${props => props.fullWidth && css`
    width: 100%;
    display: flex;
  `}
  
  ${props => props.primary && css`
    background: var(--gradient-primary);
    color: white;
    box-shadow: var(--shadow-md);
    
    &:hover {
      box-shadow: var(--shadow-xl);
      transform: translateY(-2px);
    }
    
    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
      transition: left var(--transition-slow);
    }
    
    &:hover:before {
      left: 100%;
    }
  `}
  
  ${props => props.secondary && css`
    background: transparent;
    border: 2px solid var(--primary-color);
    color: var(--primary-color);
    box-shadow: var(--shadow-sm);
    
    &:hover {
      background: var(--primary-color);
      color: white;
      box-shadow: var(--shadow-lg);
      transform: translateY(-2px);
    }
  `}
  
  ${props => props.accent && css`
    background: var(--accent-color);
    color: white;
    box-shadow: var(--shadow-md);
    
    &:hover {
      box-shadow: var(--shadow-xl);
      transform: translateY(-2px);
      background: var(--accent-dark);
    }
  `}
  
  ${props => props.outline && css`
    background: transparent;
    border: 2px solid ${props => props.light ? 'white' : 'var(--primary-color)'};
    color: ${props => props.light ? 'white' : 'var(--primary-color)'};
    box-shadow: none;
    
    &:hover {
      background: ${props => props.light ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
      border-color: ${props => props.light ? 'white' : 'var(--primary-dark)'};
      color: ${props => props.light ? 'white' : 'var(--primary-dark)'};
      transform: translateY(-2px);
    }
  `}
  
  ${props => props.ghost && css`
    background: transparent;
    color: ${props => props.light ? 'white' : 'var(--primary-color)'};
    box-shadow: none;
    
    &:hover {
      background: ${props => props.light ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
      color: ${props => props.light ? 'white' : 'var(--primary-dark)'};
      transform: translateY(-1px);
    }
  `}
  
  ${props => props.disabled && css`
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
  `}
  
  /* Loading state */
  ${props => props.loading && css`
    position: relative;
    color: transparent;
    
    &:after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 20px;
      height: 20px;
      margin: -10px 0 0 -10px;
      border: 2px solid transparent;
      border-top: 2px solid currentColor;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
  `}
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const StyledButton = styled(motion.button)`
  ${ButtonStyles}
`;

const StyledLink = styled(motion(Link))`
  ${ButtonStyles}
`;

const StyledAnchor = styled(motion.a)`
  ${ButtonStyles}
`;

const Button = ({ 
  children, 
  to, 
  href, 
  primary = false,
  secondary = false,
  accent = false,
  outline = false,
  ghost = false,
  small = false, 
  fullWidth = false,
  rounded = false,
  light = false,
  disabled = false,
  loading = false,
  className,
  ...props 
}) => {
  const commonProps = {
    primary,
    secondary,
    accent,
    outline,
    ghost,
    small,
    fullWidth,
    rounded,
    light,
    disabled,
    loading,
    className,
    whileHover: { scale: 1.02 },
    whileTap: { scale: 0.98 },
    ...props
  };
  
  // If it's a link within the app
  if (to) {
    return (
      <StyledLink 
        to={to} 
        {...commonProps}
      >
        {children}
      </StyledLink>
    );
  }
  
  // If it's an external link
  if (href) {
    return (
      <StyledAnchor 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer" 
        {...commonProps}
      >
        {children}
      </StyledAnchor>
    );
  }
  
  // Default button
  return (
    <StyledButton 
      {...commonProps}
    >
      {children}
    </StyledButton>
  );
};

export default Button; 